/* eslint-disable react/prop-types, react/destructuring-assignment */
import { isEmpty } from 'lodash'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { ModalPanel, ModalSidebar, TabSwitch } from '../../../../components/ModalSidebar'
import { ToastContext } from '../../../../context/ToastContext'
import { getEmployee } from '../../../../crud/employees.crud'
import { isSecureEnabledForTenant, isSecurePremiumEnabledForTenant } from '../../../../crud/orgs.crud'
import useAllEmployees from '../../../../hooks/useAllEmployees'
import useGroupsFetch from '../../../../hooks/useGroupsFetch'
import CopyButton from '../../../../partials/content/CopyButton'
import { adaptGroupsToStandardFormat } from '../../../../util/groups.util'
import { normalizeEmployeeLicenseType } from '../../../../util/employeeLicenses.util'
import { getEmployeeFullName } from '../../../../util/table.util'
import { GWS } from '../../engage/utils'
import { resolveDirectoryIntegrationForModal } from '../../utils/integrations'
import EditEmployeeDataModal from '../EditEmployeeDataModal'
import IntegrationModals, { useIntegrationModals } from '../IntegrationModals'
import EmployeeActionsModals from './EmployeeActionsModals'
import Overview from './Overview'
import UserProperties from './UserProperties'
import { getEmployeeInitials, TABS } from './utils'

const modalInitialState = {
  selectedEmployee: null,
  openedModalName: null,
}

function ViewEmployeeDataModal(props) {
  const {
    show,
    onHide,
    tableRowEmployee,
    selectedCustomer,
    intl,
    overrideEngageLicensing = false,
    allowRemediation = false,
    allEmployeesGroups = [],
    allEmployees = [],
    onUpdate,
    selectedIntegration: selectedIntegrationFromPage,
    selectedIntegrationType: selectedIntegrationTypeFromPage,
  } = props

  const [isLoading, setLoading] = useState(true)
  const [employee, setEmployee] = useState({})

  // For integrated users the identifier is employeeId; for non-integrated users it is id.
  const tableRowIdentifier = tableRowEmployee?.employeeId || tableRowEmployee?.id
  // Loading is keyed on that identifier, not on the row object: callers rebuild an equal row object
  // on their own renders, and refetching for the employee already displayed would flash the panel.
  // The ref carries the newest row data into the fetch without retriggering it.
  const tableRowEmployeeRef = useRef(tableRowEmployee)
  // Callers that resolve the identifier asynchronously pass the row twice: pending first, resolved
  // after. That transition has to reach the fetch even when the identifier itself does not change.
  const isPendingEmployeeIdResolution = !!tableRowEmployee?.pendingEmployeeIdResolution

  const [showEditModal, setShowEditModal] = useState(false)

  // License assignment state
  const [discoverLicensesEnable, setDiscoverLicensesEnable] = useState(false)
  const [engageLicensesEnable, setEngageLicensesEnable] = useState(false)
  const [secureLicensesEnable, setSecureLicensesEnable] = useState(false)

  const [isSecureEnabled, setIsSecureEnabled] = useState(false)
  const [isSecurePremiumEnabled, setIsSecurePremiumEnabled] = useState(false)

  const [numDiscoverAvailable, setNumDiscoverAvailable] = useState(0)
  const [numEngageAvailable, setNumEngageAvailable] = useState(0)
  const [numSecureAvailable, setNumSecureAvailable] = useState(0)

  // IntegrationModals hook (for the 4 basic actions)
  const { closeIntegrationModal, modalData, openedModal, openIntegrationModal } = useIntegrationModals()

  // Modal state management (similar to Engage.js pattern) for IntegrationManager and other modals
  const [modal, setEngageModal] = useState(modalInitialState)
  const [action, setAction] = useState(null)

  // Global state
  const currentUser = useSelector(({ user }) => user.data)
  const orgLicenses = useSelector(({ licenses }) => licenses.data)
  const permissions = useSelector(({ userPermissions }) => userPermissions?.data?.permissions ?? {})

  // User modification actions require Engage Manage (on Secure pages and everywhere).
  const canPerformActions = useMemo(() => permissions.engage === 'manage', [permissions.engage])
  const reduxSelectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const reduxIntegrations = useSelector(({ integrations }) => integrations.integrations)
  const directoryIntegration = useMemo(
    () =>
      resolveDirectoryIntegrationForModal({
        selectedIntegration: selectedIntegrationFromPage,
        selectedIntegrationType: selectedIntegrationTypeFromPage,
        reduxSelectedIntegration,
        integrations: reduxIntegrations,
        customerId: selectedCustomer?.id,
      }),
    [
      selectedIntegrationFromPage,
      selectedIntegrationTypeFromPage,
      reduxSelectedIntegration,
      reduxIntegrations,
      selectedCustomer?.id,
    ],
  )
  const integrationApplicationName = directoryIntegration?.IntegrationApplication?.application_name
  const isGws = integrationApplicationName === GWS

  // Fetch groups if not provided via props
  const { fetchGroups, groups } = useGroupsFetch()

  // Use centralized hook for employee fetching
  const { allEmployees: allEmployeesFromHook } = useAllEmployees({
    allEmployees,
    selectedCustomer,
    show,
  })

  // Use prop allEmployeesGroups if available, otherwise use fetched groups
  let allEmployeesGroupsFinal = []
  if (allEmployeesGroups.length > 0) {
    allEmployeesGroupsFinal = allEmployeesGroups
  } else if (groups.length > 0) {
    allEmployeesGroupsFinal = adaptGroupsToStandardFormat(groups)
  }

  // Toast notifications
  const [, setToastInfo] = useContext(ToastContext)

  // Update license enable states when employee data changes
  useEffect(() => {
    if (show && employee?.displayLicenses) {
      setDiscoverLicensesEnable(employee.displayLicenses.discover.assigned)
      setEngageLicensesEnable(employee.displayLicenses.engage.assigned)
      setSecureLicensesEnable(employee.displayLicenses.secure.assigned)
    }
  }, [show, employee])

  // Calculate available licenses from orgLicenses
  useEffect(() => {
    if (orgLicenses && selectedCustomer) {
      let numDiscover = 0
      let numEngage = 0
      let numSecure = 0

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const license in orgLicenses) {
        if (!selectedCustomer.parent_id) {
          if (license === 'nfr-discover-essentials') {
            numDiscover += orgLicenses[license].remaining
          }
        }
        if (license === 'discover-essentials' || license === 'discover-premium') {
          numDiscover += orgLicenses[license].remaining
        }
        if (!selectedCustomer.parent_id) {
          if (license === 'nfr-engage-essentials') {
            numEngage += orgLicenses[license].remaining
          }
        }
        if (license === 'engage-essentials' || license === 'engage-premium') {
          numEngage += orgLicenses[license].remaining
        }

        if (license === 'secure-essentials') {
          numSecure += orgLicenses[license].remaining
        }
      }

      setNumDiscoverAvailable(numDiscover)
      setNumEngageAvailable(numEngage)
      setNumSecureAvailable(numSecure)
    }
  }, [orgLicenses, selectedCustomer]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    tableRowEmployeeRef.current = tableRowEmployee
  }, [tableRowEmployee])

  useEffect(() => {
    const tableRow = tableRowEmployeeRef.current

    // Normalize license arrays so null/undefined entries never cause issues
    const safeM365 = Array.isArray(tableRow?.m365Licenses) ? tableRow.m365Licenses.filter((l) => l != null) : undefined
    const safeLicenseType = Array.isArray(tableRow?.licenseType)
      ? tableRow.licenseType.filter((l) => l != null)
      : undefined

    // Seed from the table row only when the panel switches to another employee. Re-running for the
    // employee already displayed must keep the loaded data instead of clearing it.
    setEmployee((currentEmployee) => {
      const currentIdentifier = currentEmployee?.employeeId || currentEmployee?.id
      if (currentIdentifier && currentIdentifier === tableRowIdentifier) {
        return currentEmployee
      }
      return {
        ...tableRow,
        ...(safeM365 !== undefined && { m365Licenses: safeM365 }),
        ...(safeLicenseType !== undefined && { licenseType: safeLicenseType }),
      }
    })

    const fetchEmployee = async () => {
      if (isPendingEmployeeIdResolution) {
        setLoading(false)
        return
      }

      setLoading(true)

      if (isEmpty(selectedCustomer) || isEmpty(tableRow) || !tableRowIdentifier) {
        setLoading(false)
        return
      }

      // load IsSecureEnabled
      const includeNFR = tableRow.licenses?.includes('nfr-secure-essentials')
      const responses = await Promise.all([
        isSecureEnabledForTenant(selectedCustomer.id, false),
        isSecurePremiumEnabledForTenant(selectedCustomer.id, includeNFR),
        getEmployee(tableRowIdentifier),
      ])
      setIsSecureEnabled(responses[0])
      setIsSecurePremiumEnabled(responses[1])

      // Get the latest employee data
      let latestEmployee = responses[2]?.data

      // Filter out null entries from m365Licenses if present
      if (latestEmployee?.m365Licenses && Array.isArray(latestEmployee.m365Licenses)) {
        latestEmployee.m365Licenses = latestEmployee.m365Licenses.filter((license) => license != null)
      }

      latestEmployee = normalizeEmployeeLicenseType(latestEmployee, integrationApplicationName)

      const combinedEmployee = {
        ...tableRow,
        ...latestEmployee,
        isSecureEnabled: responses[0],
        isSecurePremiumEnabled: responses[1],
      }
      setEmployee(combinedEmployee)
      setLoading(false)
    }
    fetchEmployee()
  }, [selectedCustomer, tableRowIdentifier, isPendingEmployeeIdResolution, integrationApplicationName])

  // Check if employee is integrated
  const isIntegrated = useMemo(
    () => !!(employee?.employeeIdentifier || tableRowEmployee?.employeeIdentifier),
    [employee?.employeeIdentifier, tableRowEmployee?.employeeIdentifier],
  )

  // Fetch groups if not provided via props and user is integrated
  useEffect(() => {
    if (show && selectedCustomer?.id && allEmployeesGroups.length === 0 && isIntegrated && !isGws) {
      fetchGroups({ customerId: selectedCustomer.id })
    }
  }, [show, selectedCustomer?.id, allEmployeesGroups.length, isIntegrated, isGws, fetchGroups])

  const modalTabs = useMemo(() => TABS, [])

  // Custom title component with avatar and email
  const customTitle = useMemo(
    () => (
      <div
        className="d-flex align-items-center employee-header-title"
        style={{
          gap: '1rem',
          height: '3.125rem',
          minHeight: '3.125rem',
          maxHeight: '3.125rem',
          marginBottom: '1.5rem',
          boxSizing: 'border-box',
        }}
      >
        {/* Profile Picture / Avatar */}
        <div
          className="d-flex align-items-center justify-content-center employee-header-avatar fw-bold"
          style={{
            width: '3.125rem',
            height: '3.125rem',
            borderRadius: '50%',
            backgroundColor: 'var(--bs-primary)',
            color: 'var(--bs-white)',
            fontSize: '1.2rem',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          {getEmployeeInitials(employee)}
        </div>

        {/* Name and Email */}
        <div
          className="flex-grow"
          style={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div
            className="d-flex align-items-center"
            style={{ height: '2rem', lineHeight: '2rem', minHeight: '2rem', maxHeight: '2rem' }}
          >
            {(employee.isUserSigninEnabled === false || employee.enabled === false) && (
              <span className="suspended me-2" style={{ lineHeight: '2rem' }}>
                <i className="fa fa-ban" />
              </span>
            )}
            <span className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: '2rem', display: 'inline-block' }}>
              {getEmployeeFullName(employee)}
            </span>
            {employee.ignored && (
              <span className="ms-2 badge badge-secondary" style={{ lineHeight: '2rem' }}>
                Ignored
              </span>
            )}
          </div>

          <div
            className="d-flex align-items-center"
            style={{
              gap: '0.5rem',
              height: '1.125rem',
              lineHeight: '1.125rem',
              minHeight: '1.125rem',
              maxHeight: '1.125rem',
            }}
          >
            <span
              className="font-color-2"
              style={{ fontSize: '0.95rem', lineHeight: '1.125rem', display: 'inline-block' }}
            >
              {employee.email}
            </span>
            {employee.email && (
              <CopyButton textToCopy={employee.email} className="d-inline-block" style={{ lineHeight: '1.125rem' }} />
            )}
          </div>
        </div>
      </div>
    ),
    [employee],
  )

  // Handle edit button click
  const handleEdit = () => {
    setShowEditModal(true)
  }

  // Handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false)
  }

  const refreshEmployeeData = React.useCallback(
    (showSuccessToast = false) => {
      const tableRow = tableRowEmployeeRef.current
      if (selectedCustomer && tableRowIdentifier) {
        setLoading(true)
        // Load IsSecureEnabled along with employee data
        const includeNFR = tableRow.licenses?.includes('nfr-secure-essentials')
        Promise.all([
          isSecureEnabledForTenant(selectedCustomer.id, false),
          isSecurePremiumEnabledForTenant(selectedCustomer.id, includeNFR),
          getEmployee(tableRowIdentifier),
        ])
          .then((responses) => {
            let latestEmployee = responses[2]?.data

            // Transform groupsByType to expected frontend format
            if (latestEmployee?.groupsByType && !latestEmployee?.m365Groups) {
              latestEmployee.m365Groups = latestEmployee.groupsByType.M365Group || []
              latestEmployee.securityGroups = latestEmployee.groupsByType.SecurityGroup || []
              latestEmployee.distributionGroups = latestEmployee.groupsByType.DistributionGroup || []
              latestEmployee.securityMailEnabledGroups = latestEmployee.groupsByType.SecurityMailEnabledGroup || []
              latestEmployee.sharedMailboxes = latestEmployee.groupsByType.SharedMailbox || []
            }

            latestEmployee = normalizeEmployeeLicenseType(latestEmployee, integrationApplicationName)

            const combinedEmployee = {
              ...tableRow,
              ...latestEmployee,
              isSecureEnabled: responses[0],
              isSecurePremiumEnabled: responses[1],
            }
            setEmployee(combinedEmployee)
            setIsSecureEnabled(responses[0])
            setIsSecurePremiumEnabled(responses[1])
            if (showSuccessToast) {
              setToastInfo({ show: true, type: 'GENERAL.ACTION_SUCCESS' })
            }
          })
          .catch((error) => {
            console.error('Error refreshing employee:', error)
            setToastInfo({
              show: true,
              type: error?.response?.data?.message || error?.message || 'GENERAL.ACTION_FAILED',
            })
          })
          .finally(() => {
            setLoading(false)
          })
      }
    },
    [selectedCustomer, tableRowIdentifier, integrationApplicationName, setToastInfo],
  )

  const notifyListUpdated = React.useCallback(() => {
    onUpdate?.()
  }, [onUpdate])

  const handleEmployeeMutationComplete = React.useCallback(
    (showSuccessToast = false) => {
      refreshEmployeeData(showSuccessToast)
      notifyListUpdated()
    },
    [notifyListUpdated, refreshEmployeeData],
  )

  // Handle edit modal update (refresh employee data and the source list after the hub task)
  const handleEditModalUpdate = () => {
    handleEmployeeMutationComplete(false)
    setShowEditModal(false)
  }

  // Modal management functions (similar to Engage.js)
  const closeModal = React.useCallback(() => {
    setEngageModal(modalInitialState)
    setAction(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setModal = React.useCallback((modalConfig) => {
    if (modalConfig?.selectedEmployee) {
      setEngageModal({
        selectedEmployee: modalConfig.selectedEmployee,
        openedModalName: modalConfig.openedModalName,
      })
    } else {
      setEngageModal({
        selectedEmployee: null,
        openedModalName: modalConfig?.openedModalName,
      })
    }
  }, [])

  const setIntegrationManagerAction = React.useCallback((actionValue) => {
    setAction(actionValue)
  }, [])

  // Track when any modal is open
  const isIntegrationModalOpen = !!modal.openedModalName || !!openedModal

  return (
    <>
      <ModalSidebar
        show={show}
        onHide={onHide}
        size="lg"
        title={customTitle}
        isLoading={isLoading}
        toBackdrop={(isIntegrationModalOpen || showEditModal) && show}
      >
        <ModalPanel leftButton="close" tabs={modalTabs}>
          <TabSwitch tab="overview">
            <Overview
              employee={employee}
              selectedCustomer={selectedCustomer}
              onEdit={handleEdit}
              allowRemediation={allowRemediation}
              intl={intl}
              currentUser={currentUser}
              orgLicenses={orgLicenses}
              overrideEngageLicensing={overrideEngageLicensing}
              canPerformActions={canPerformActions}
              isLoading={isLoading}
              setLoading={setLoading}
              discoverLicensesEnable={discoverLicensesEnable}
              setDiscoverLicensesEnable={setDiscoverLicensesEnable}
              engageLicensesEnable={engageLicensesEnable}
              setEngageLicensesEnable={setEngageLicensesEnable}
              secureLicensesEnable={secureLicensesEnable}
              setSecureLicensesEnable={setSecureLicensesEnable}
              isSecureEnabled={isSecureEnabled}
              isSecurePremiumEnabled={isSecurePremiumEnabled}
              numDiscoverAvailable={numDiscoverAvailable}
              numEngageAvailable={numEngageAvailable}
              numSecureAvailable={numSecureAvailable}
              setAction={setIntegrationManagerAction}
              setModal={setModal}
              openIntegrationModal={openIntegrationModal}
              selectedIntegration={directoryIntegration}
            />
          </TabSwitch>

          <TabSwitch tab="userproperties">
            <UserProperties
              employee={employee}
              intl={intl}
              selectedCustomer={selectedCustomer}
              onEdit={handleEdit}
              overrideEngageLicensing={overrideEngageLicensing}
              canPerformActions={canPerformActions}
              setAction={setIntegrationManagerAction}
              setModal={setModal}
              openIntegrationModal={openIntegrationModal}
              selectedIntegration={directoryIntegration}
            />
          </TabSwitch>
        </ModalPanel>
      </ModalSidebar>

      {/* IntegrationModals - handles the 4 basic actions (reset-password, block-signin, etc.) */}
      <IntegrationModals
        closeIntegrationModal={closeIntegrationModal}
        modalData={modalData}
        onSuccessUpdateIntegrationManager={() => {
          handleEmployeeMutationComplete(true)
        }}
        openedModal={openedModal}
        selectedEmployees={[employee]}
        onBackToEmployeeModal={closeIntegrationModal}
      />

      {/* Extended modals - handles IntegrationManager and other modals */}
      <EmployeeActionsModals
        action={action}
        modal={modal}
        closeModal={closeModal}
        employee={employee}
        selectedCustomer={selectedCustomer}
        allEmployeesGroups={allEmployeesGroupsFinal}
        allEmployees={allEmployeesFromHook}
        selectedIntegration={directoryIntegration}
        onUpdate={handleEmployeeMutationComplete}
      />

      <EditEmployeeDataModal
        show={showEditModal}
        onHide={handleEditModalClose}
        onUpdate={handleEditModalUpdate}
        employee={employee}
        selectedCustomer={selectedCustomer}
        allEmployeesGroups={allEmployeesGroupsFinal}
        allEmployees={allEmployeesFromHook}
        headerBackButtonAction={handleEditModalClose}
        selectedIntegration={directoryIntegration}
        selectedIntegrationType={selectedIntegrationTypeFromPage}
      />
    </>
  )
}

export default injectIntl(ViewEmployeeDataModal)
